/**
 * Metraj hesabi ve otomatik birim secimi.
 *
 * Bolum 10.2 #1 — "Otomatik m2/m3 birimi (yukseklik yoksa alan, varsa hacim)".
 * Prototipteki `birim()` fonksiyonu bu kurali biliyordu ama `rowM3()` her zaman
 * en*boy*yuk hesapliyordu; yukseklik girilmemis satirlar 0 doner ve metraj kaybolurdu.
 * Burada duzeltilmis hali var: yukseklik yoksa ALAN hesaplanir.
 */
/**
 * ISKELE TIPI — olcunun NASIL hesaplanacagini belirler (musteri karari 31.07.2026).
 *
 *   normal → hacimli iskele.  en x boy x yuk          → m3
 *   asma   → asma iskele.     en x boy                → m2
 *   kapama → dairesel kapama. pi x r^2                → m2
 *
 * ⚠️ Tip, "yukseklik girildi mi" gibi bir CIKARIM degil, ACIK bir secimdir.
 *    Onceki hâlde yukseklik bos birakilan hacimli bir iskele sessizce ALAN
 *    olarak hesaplaniyordu; sahada bunu fark etmenin yolu yoktu.
 */
export const ISKELE_TIPLERI = ['normal', 'asma', 'kapama'];
export const ISKELE_TIPI_ETIKET = {
    normal: 'Normal (hacimli)',
    asma: 'Asma (alan)',
    kapama: 'Kapama (daire)',
};
/**
 * Satirin tipi. Kayitli degilse ESKI DAVRANISTAN turetilir: yukseklik varsa
 * hacimli, yoksa alan. `kapama` turetilemez — o tip bu degisiklikle geldi ve
 * eski veride karsiligi yok.
 */
export function olcuTipi(o) {
    if (o.tip && ISKELE_TIPLERI.includes(o.tip))
        return o.tip;
    return num(o.yuk) > 0 ? 'normal' : 'asma';
}
/** Dairesel kapama alani — pi x r^2. */
export function daireAlani(yaricap) {
    const r = num(yaricap);
    return round2(Math.PI * r * r);
}
export const BIRIM_LABEL = {
    m2: 'm²',
    m3: 'm³',
};
const num = (v) => {
    const n = typeof v === 'number' ? v : Number.parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : 0;
};
/** Bir olcu satirinda yukseklik var mi? */
export function hasYukseklik(o) {
    return num(o.yuk) > 0;
}
/** Satirin KENDI birimi — tipinden gelir. */
export function olcuBirimi(o) {
    return olcuTipi(o) === 'normal' ? 'm3' : 'm2';
}
/**
 * ESDEGER KATSAYISI — m2 metraji m3 cinsinden ifade etmek icin (musteri, 02.08.2026):
 * "yalnizca m2 cinsinden hesaplanan islemler x4 katsayisi kullanilarak m3
 * cinsine donusturulup raporlanabilir".
 *
 * ⚠️ Bu bir MUHENDISLIK donusumu degil, musterinin ticari kabulu. Tek bir
 *    karsilastirilabilir sayi gerektiginde kullanilir; olcu satirlarina
 *    ASLA uygulanmaz, yalnizca TOPLAMA.
 *
 * ⚠️ m3 ICIN KATSAYI YOKTUR — 06.08.2026'da eklendi, 07.08.2026'da GERI ALINDI.
 *
 *    Musterinin kumulatif dosyasinda iki metraj sutunu var:
 *      Metraj-Sa   = 1.09 * 2.07 * 7.2                    (ham olcu)
 *      Metraj,Grup = m3 ise Sa*1.5, m2 ise Sa*4, A/S ise Sa
 *
 *    "Sitedeki toplam gercekte olandan az" diye bir bildirim gelince, eksik
 *    olanin Grup sutunundaki x1.5 oldugu dusunuldu ve eklendi. YANLISTI:
 *
 *      · Yon ters aktarilmis. Site AZ degil FAZLA gosteriyordu
 *        (site 1.806,37 m3 · Excel Metraj-Sa 1.544,96).
 *      · Santiye sefi (Bedirhan, 07.08.2026) net soyledi: sistem METRAJ SA
 *        esas almali, iki toplam "ayni olmali, en fazla 10-20 m3 fark".
 *      · x1.5 uygulansaydi sistem 2.700+ m3 gosterecekti — toleransin
 *        elli kati sapma.
 *
 *    Grup sutunu Mod'un HAKEDIS hesabina ait; bu sistem bir hakedis sistemi
 *    degil (CLAUDE.md). Ham olcuyu tasimasi DOGRU davranistir.
 *
 * ⚠️ Bir sonraki "toplam tutmuyor" bildirimi geldiginde ONCE YON OLCULSUN:
 *    hangi sayi hangisinden buyuk. Bu tur bir bildirimde yon, cozumun tamamini
 *    belirliyor ve sozlu aktarimda kolayca tersine donuyor.
 */
export const ESDEGER_KATSAYI = 4;
/**
 * Tek satirin metraji — SATIRIN KENDI biriminde.
 *
 * ⚠️ 02.08.2026'da `birim` parametresi KALDIRILDI. Onceden isin birimi
 *    parametre olarak geliyordu ve karisik iste hacimli satir ALANA
 *    indirgeniyordu: 5x4x6 = 120 m3 olan bir satir 20 m2 olarak hesaplaniyor,
 *    6 metrelik yukseklik SESSIZCE dusuyordu. Tek iste 100 m3 kayip.
 *
 *    Musteri karari acik: "sistem metraji iki farkli sekilde hesaplamalidir:
 *    alan (m2) ve hacim (m3)". Artik hicbir satir baska bir birime cevrilmez.
 */
export function olcuMetraj(o) {
    const tip = olcuTipi(o);
    if (tip === 'kapama')
        return daireAlani(num(o.yaricap));
    if (tip === 'asma')
        return round2(num(o.en) * num(o.boy));
    return round2(num(o.en) * num(o.boy) * num(o.yuk));
}
/**
 * Satirin hesabini INSAN DILINDE yazar: "5,00 × 4,00 × 2,00 = 40,00 m³".
 *
 * Sahada iki soru soruluyor ve ikisi de bu satirla cevaplaniyor: "hangi olculer
 * girilmis" ve "bu sayi nereden cikti". Yalnizca sonucu gostermek, yanlis
 * girilmis bir olcuyu gorunmez kilar.
 */
export function olcuAciklama(o) {
    const tip = olcuTipi(o);
    const sonuc = olcuMetraj(o);
    const b = BIRIM_LABEL[olcuBirimi(o)];
    if (tip === 'kapama') {
        return `π × ${fmtMetraj(num(o.yaricap))}² = ${fmtMetraj(sonuc)} ${b}`;
    }
    const carpim = tip === 'normal'
        ? `${fmtMetraj(num(o.en))} × ${fmtMetraj(num(o.boy))} × ${fmtMetraj(num(o.yuk))}`
        : `${fmtMetraj(num(o.en))} × ${fmtMetraj(num(o.boy))}`;
    return `${carpim} = ${fmtMetraj(sonuc)} ${b}`;
}
/**
 * ESDEGER m3 — iki ham toplami tek sayiya indirger: `m3 + m2 x 4`.
 *
 * ⚠️ TEK YER. Ayni carpim uc ayri dosyada elle yaziliydi; 06.08'de katsayi
 *    denemesi yapilirken biri atlansaydi pano ile gunluk rapor sessizce
 *    ayrisirdi. Deneme geri alindi ama tek kaynak KALDI — bir dahakine
 *    degisiklik tek satirda olur.
 */
export function esdegerMetraj(m3, m2) {
    return round2(m3 + m2 * ESDEGER_KATSAYI);
}
/**
 * Isin metraj ozeti — IKI AYRI TOPLAM.
 *
 * ⚠️ Tek bir "is birimi" KAVRAMI KALKTI. Bir is emri hem hacimli hem asma
 *    iskele icerebilir (K11) ve bunlari tek sayiya sikistirmak, birinin
 *    bilgisini yok etmek demekti.
 */
export function metrajOzeti(olculer) {
    const satirlar = olculer.map((o) => ({ metraj: olcuMetraj(o), birim: olcuBirimi(o) }));
    const topla = (b) => round2(satirlar.filter((s) => s.birim === b).reduce((a, s) => a + s.metraj, 0));
    const m3 = topla('m3');
    const m2 = topla('m2');
    return { m3, m2, esdegerM3: esdegerMetraj(m3, m2), satirlar };
}
/** Iki toplami insan diline cevirir: "120,00 m3 · 25,63 m2" */
export function metrajYazi(o) {
    const p = [];
    if (o.m3 > 0)
        p.push(`${fmtMetraj(o.m3)} ${BIRIM_LABEL.m3}`);
    if (o.m2 > 0)
        p.push(`${fmtMetraj(o.m2)} ${BIRIM_LABEL.m2}`);
    return p.join(' · ') || '—';
}
/**
 * K7 — hakedis insan-saat. Kira YOKTUR; kurulu m3 takip/musteri raporu verisidir.
 * Gunluk rapordaki "Harcanan" kolonu = kisi x saat.
 */
export function adamSaat(kisiSayisi, baslangic, bitis) {
    const saat = (bitis.getTime() - baslangic.getTime()) / 3_600_000;
    if (!Number.isFinite(saat) || saat <= 0)
        return 0;
    return round2(Math.max(0, kisiSayisi) * saat);
}
/** Verimlilik: birim zamanda kurulan metraj. Bolum 1.3'un birinci hedefi. */
export function verimlilik(metraj, adamSaatDegeri) {
    if (adamSaatDegeri <= 0)
        return null;
    return round2(metraj / adamSaatDegeri);
}
function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
/** Turkce sayi formati — sahada virgul kullaniliyor (2,07 / 3,20). */
export function fmtMetraj(n) {
    if (n == null || !Number.isFinite(n))
        return '—';
    return n.toFixed(2).replace('.', ',');
}
//# sourceMappingURL=measurement.js.map